import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { demoNovel, localMvpOwnerId, type GenerationJob, type OAuthProvider } from "@lorevok/domain";
import {
  ApiError,
  createGenerationJob,
  createTranslateJob,
  deleteAccount,
  extendNovel,
  isGoneJobError,
  loadGenerationJob,
  loadMyNovelsPage,
  loadCatalogPage,
  loadNovelForReading,
  NOVEL_LIST_PAGE_SIZE_MAX,
  cachedSiteConfig,
  loadSiteConfig,
  setNotInBetaHandler,
  type AccountBlockReason,
  type SiteConfig,
  type NovelCatalogResult,
  type NovelListSource,
  type NovelSummary
} from "./apiClient.js";
import { useCredits } from "./lib/useCredits.js";
import { useRefreshOnJobStatus } from "./lib/useRefreshOnJobStatus.js";
import { operationCredits } from "./lib/credits.js";
import { useMe } from "./lib/useMe.js";
import {
  ACTIVE_JOB_KEY,
  isNovelFinished,
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet
} from "./lib/storage.js";
import { Topbar } from "./components/Topbar.js";
import { GenerationStartedDialog } from "./components/GenerationStartedDialog.js";
import { estimateGenerationTimeKey } from "./lib/generationEstimate.js";
import { pickSuggestedNovel } from "./lib/suggestNovel.js";
import { buildPath, parseRoute, type RouteState } from "./lib/route.js";
import { campaignSearch } from "./lib/campaignParams.js";
import { isKnownPath, routePageMeta, usePageMeta } from "./lib/pageMeta.js";
import {
  NO_CATALOG_FILTERS,
  catalogFilterSearch,
  hasCatalogFilters,
  parseCatalogFilters,
  sameCatalogFilters,
  withSearch,
  type CatalogFilters
} from "./lib/catalogFilters.js";
import {
  knownNovelId,
  needsLinkResolution,
  novelLinkSegment,
  resolveNovelLink,
  useNovelLinksVersion
} from "./lib/novelLinks.js";
import { onApiBack } from "./lib/apiStatus.js";
import { authorRouteKey } from "./lib/authorRoute.js";
import { useLanguage, useT, type TranslationKey } from "./lib/i18n/store.js";
import { isAuthEnabled } from "./lib/supabaseClient.js";
import { isOwnerPageBlocked } from "./lib/authGate.js";
import { useSession } from "./lib/useSession.js";
import { onPasswordRecovery, signOut } from "./lib/auth.js";
import { initAnalytics, identifyUser, resetAnalytics, trackPageView, track } from "./lib/analytics.js";
import { AuthPage, AuthPageSkeleton, NewPasswordPage, type ReaderPrompt } from "./pages/AuthPage.js";
import { rememberAuthReturn, takeAuthReturn } from "./lib/authReturn.js";
import {
  accountOAuthProvider,
  gatedPageTarget,
  rememberOAuthReturnTarget,
  takeOAuthReturnTarget,
  takeOAuthStart
} from "./lib/oauthReturn.js";
import { FinishSignUpPage } from "./pages/FinishSignUpPage.js";
import { LandingPage } from "./pages/LandingPage.js";
import { LegalDocumentPage } from "./pages/LegalDocumentPage.js";
import { PricingPage } from "./pages/PricingPage.js";
import { SiteFooter } from "./components/SiteFooter.js";
import { CookieBanner } from "./components/CookieBanner.js";
import { AdminPageSkeleton, RouteFallback } from "./components/RouteSkeletons.js";
import { AccountBlockedScreen } from "./components/AccountBlockedScreen.js";
import { CatalogPage } from "./pages/CatalogPage.js";
import { NovelDetailPage } from "./pages/NovelDetailPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { AuthorPage } from "./pages/AuthorPage.js";
import type { Page } from "./types.js";
import { NOVEL_TRANSLATION_ENABLED } from "./lib/features.js";

const AdminPage = lazy(() => import("./pages/AdminPage.js").then((module) => ({ default: module.AdminPage })));
const EditorPage = lazy(() => import("./pages/EditorPage.js").then((module) => ({ default: module.EditorPage })));
const PlayerPage = lazy(() => import("./pages/PlayerPage.js").then((module) => ({ default: module.PlayerPage })));
const CreatePage = lazy(() => import("./pages/CreatePage.js").then((module) => ({ default: module.CreatePage })));
const GenerationStatusPage = lazy(() => import("./pages/GenerationStatusPage.js").then((module) => ({ default: module.GenerationStatusPage })));
const GenerationLoopPage = lazy(() => import("./pages/GenerationLoopPage.js").then((module) => ({ default: module.GenerationLoopPage })));
// The legal documents are separate pages (/terms, /privacy, /refund) that stay
// reachable in every shell, signed in or not.
function isLegalPage(page: Page): page is "terms" | "privacy" | "refund" {
  return page === "terms" || page === "privacy" || page === "refund";
}

// App-like, viewport-filling views where a page footer would only add a stray
// scroll; their legal links live in the topbar menu instead.
const FOOTERLESS_PAGES: ReadonlySet<Page> = new Set(["editor", "player", "status", "genloop"]);

const ShowcasePage = lazy(() => import("./pages/ShowcasePage.js").then((module) => ({ default: module.ShowcasePage })));

interface NovelListState {
  novels: NovelSummary[];
  source: NovelListSource;
  loaded: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  moreFailed: boolean;
  loadMore: () => Promise<void>;
  /** Load every remaining page (a sort other than the list's own order needs them all). */
  loadAll: () => Promise<void>;
  refresh: (options?: { silent?: boolean }) => Promise<void>;
  /** In-place patch (optimistic badge flip, synchronous delete filter). */
  update: (updater: (current: NovelSummary[]) => NovelSummary[]) => void;
}

/**
 * App-owned async novel list. Pages show skeletons while `loaded` is false and
 * render `novels` otherwise; the refresh policy lives here, once:
 * - a sequence guard drops stale responses (requests can overlap when the
 *   session resolves at boot);
 * - silent refreshes (after a mutation) keep the current list on screen and
 *   swap the data in place — and never downgrade it: "fallback" means the
 *   request got no usable answer (the mutation just evicted the offline
 *   cache), so the current list is still the best truth available;
 * - non-silent refreshes (identity change) re-arm the skeletons, and on the
 *   (today unreachable) rejection path clear the list rather than re-present
 *   rows that may belong to a previous identity.
 */
function useNovelList(
  loader: (offset?: number, limit?: number) => Promise<NovelCatalogResult>
): NovelListState {
  const [novels, setNovels] = useState<NovelSummary[]>([]);
  const [source, setSource] = useState<NovelListSource>("fallback");
  const [loaded, setLoaded] = useState(false);
  const seqRef = useRef(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const moreInFlight = useRef(false);
  const refreshPending = useRef(false);
  // Whether any load has ever written data — a silent refresh that races the
  // very first load must not present the initial empty list as loaded.
  const everLoadedRef = useRef(false);

  const refresh = useCallback(
    ({ silent = false } = {}): Promise<void> => {
      const run = (isSilent: boolean): Promise<void> => {
        const seq = ++seqRef.current;
        refreshPending.current = true;
        moreInFlight.current = false;
        setLoadingMore(isSilent);
        setMoreFailed(false);
        if (!isSilent) setLoaded(false);
        const finish = () => {
          refreshPending.current = false;
          setLoadingMore(false);
          everLoadedRef.current = true;
          setLoaded(true);
        };
        return loader().then(
          (result) => {
            if (seq !== seqRef.current) return;
            if (isSilent && result.source === "fallback") {
              if (!everLoadedRef.current) {
                // This silent refresh invalidated the still-in-flight first
                // load — there is nothing on screen to preserve, so run a
                // full load instead of marking an empty list as loaded.
                return run(false);
              }
              // Keep the rows, but stop claiming they are live server data:
              // the refetch got no usable answer, so what's on screen is a
              // last-known copy.
              setSource((current) => (current === "api" ? "cache" : current));
              finish();
              return;
            }
            setNovels(result.novels);
            setNextOffset(result.nextOffset ?? null);
            setSource(result.source);
            finish();
          },
          () => {
            if (seq !== seqRef.current) return;
            if (!isSilent) {
              setNovels([]);
              setNextOffset(null);
              setSource("fallback");
            }
            finish();
          }
        );
      };
      return run(silent);
    },
    [loader]
  );

  // One page ("Load more"), or every remaining page in the API's largest pages
  // (`loadAll`) — as one run, so a refresh or identity change stops it cleanly.
  const loadPages = useCallback(async (all: boolean) => {
    if (nextOffset === null || moreInFlight.current || refreshPending.current || !loaded) return;
    const seq = seqRef.current;
    moreInFlight.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      let offset: number | null = nextOffset;
      while (offset !== null) {
        const from: number = offset;
        const result = await loader(from, all ? NOVEL_LIST_PAGE_SIZE_MAX : undefined);
        if (seq !== seqRef.current) return;
        if (result.source !== "api") throw new Error("Page unavailable");
        setNovels((current) => {
          const ids = new Set(current.map((novel) => novel.id));
          return [...current, ...result.novels.filter((novel) => !ids.has(novel.id))];
        });
        offset = result.nextOffset != null && result.nextOffset > from ? result.nextOffset : null;
        setNextOffset(offset);
        if (!all) break;
      }
    } catch {
      if (seq === seqRef.current) setMoreFailed(true);
    } finally {
      if (seq === seqRef.current) {
        moreInFlight.current = false;
        setLoadingMore(false);
      }
    }
  }, [nextOffset, loaded, loader]);
  const loadMore = useCallback(() => loadPages(false), [loadPages]);
  const loadAll = useCallback(() => loadPages(true), [loadPages]);
  return {
    novels, source, loaded, refresh, update: setNovels, hasMore: nextOffset !== null, loadingMore, moreFailed,
    loadMore, loadAll
  };
}

// Requested machine-translation languages keyed by generation job id, persisted
// so a reload during the multi-minute generation window doesn't silently drop
// the author's localize-on-create choice.
const PENDING_TRANSLATIONS_KEY = "lorevok:pendingTranslations";
function readPendingTranslations(): Record<string, string[]> {
  try {
    const raw = safeLocalStorageGet(PENDING_TRANSLATIONS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
  } catch {
    return {};
  }
}
function writePendingTranslations(map: Map<string, string[]>): void {
  safeLocalStorageSet(PENDING_TRANSLATIONS_KEY, JSON.stringify(Object.fromEntries(map)));
}

/** Retry interval for re-attaching a remembered job while the API is unreachable. */
const JOB_RETRY_MS = 10_000;

/**
 * Why a generation could not start, as a short code for analytics: the API's
 * error code (insufficient_credits, content_rejected, …), its HTTP status when
 * it sent none, "network" when the API was unreachable (fetch throws a
 * TypeError), else "unknown". Never the message, which can quote the idea.
 */
function startFailureReason(error: unknown): string {
  if (error instanceof ApiError) {
    // Only a code-shaped value: a proxy in front of the API may put prose here.
    return error.code && /^[a-z0-9_]{1,40}$/.test(error.code) ? error.code : `http_${error.status}`;
  }
  return error instanceof TypeError ? "network" : "unknown";
}

export function App() {
  const t = useT();
  const { user, loading: sessionLoading } = useSession();
  const currentOwnerId = isAuthEnabled ? user?.id ?? null : localMvpOwnerId;
  // Credit balance + prices for the signed-in user. Null (hidden) when auth is
  // off or signed out; refreshed after every credit-spending action.
  const credits = useCredits(user?.id);
  const { refresh: refreshCredits } = credits;
  // Admin flag drives the /admin nav link and route visibility; the display
  // name is the topbar identity (refreshed after the author edits it).
  const {
    isAdmin,
    username: currentUsername,
    displayName: userDisplayName,
    avatarUrl: userAvatarUrl,
    createdAt: userCreatedAt,
    usernameConfirmed,
    loaded: meLoaded,
    refresh: refreshMe
  } = useMe(user?.id);
  const uiLanguage = useLanguage();
  // Public storefront or closed beta — decided by the API (/api/site-config).
  // Without auth (local MVP) everything is open. While the answer is loading the
  // storefront renders (a visitor never sees a sign-in wall flash first) but the
  // signed-out catalog fetch waits for it.
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(() =>
    isAuthEnabled ? cachedSiteConfig() : { openSignup: true }
  );
  // Bumped when the API comes back after being down (a redeploy), so reads the
  // page made while it was unreachable — the site mode first — are made again.
  const [apiEpoch, setApiEpoch] = useState(0);
  useEffect(() => onApiBack(() => setApiEpoch((n) => n + 1)), []);
  useEffect(() => {
    if (!isAuthEnabled) return;
    let active = true;
    void loadSiteConfig().then((config) => {
      // No answer (API down): keep the remembered mode instead of a closed gate.
      if (active && config) setSiteConfig(config);
    });
    return () => {
      active = false;
    };
  }, [apiEpoch]);
  const openSignup = siteConfig?.openSignup ?? false;
  // Fully-closed beta: with auth configured, no signed-in user, and the API in
  // invite-only mode, the entire storefront is behind the sign-in screen.
  const fullyClosed = isAuthEnabled && !sessionLoading && !user && siteConfig !== null && !openSignup;

  // Set when the API rejects this signed-in account outright: 403 not_in_beta
  // (the email is not on the closed-beta allowlist) or 403 account_suspended.
  // We surface a clear screen rather than silently signing the user out — an
  // auto-signOut loops and spams logout errors, and gives no explanation for
  // why access vanished.
  const [betaBlocked, setBetaBlocked] = useState<AccountBlockReason | null>(null);
  useEffect(() => {
    setNotInBetaHandler((reason) => setBetaBlocked(reason));
    return () => setNotInBetaHandler(null);
  }, []);
  // Clear the block whenever the identity changes (sign out / sign in), so a
  // freshly signed-in (and now allowlisted) user is not stuck on the screen.
  useEffect(() => {
    setBetaBlocked(null);
  }, [user?.id]);

  // Arrived from an emailed password-reset link: ask for the new password
  // before anything else.
  const [recoveringPassword, setRecoveringPassword] = useState(false);
  useEffect(() => onPasswordRecovery(() => setRecoveringPassword(true)), []);
  // The current page is reflected in the URL (apps/web/src/lib/route.ts) so a
  // refresh or a shared deep link restores the same screen instead of dropping
  // back to the landing page. The initial route is parsed from the address bar
  // once, and seeds `page` plus the two novel-id holders below.
  //
  // The showcase is an internal DEBUG stand, reachable ONLY in dev builds
  // (import.meta.env.DEV): route.ts refuses to parse or emit /showcase in a
  // production build, and the render below is gated on DEV too, so a production
  // build never exposes it no matter what path a visitor types.
  //
  // Parsed once (lazy initializer runs a single time); the value never changes,
  // so it stays referentially stable for the effects below that depend on it.
  const [initialRoute] = useState<RouteState>(() =>
    parseRoute(typeof window !== "undefined" ? window.location.pathname : "/")
  );
  const [page, setPage] = useState<Page>(initialRoute.page);
  // ── Analytics (PostHog: on in production builds, consent-gated) ────────
  // Initialize once and tie events to a stable person for D1/D7 retention. The
  // manual $pageview (this is a useState-routed SPA, so URL-based autocapture
  // would miss most navigations) is sent further down, after the address bar
  // has caught up with the page.
  useEffect(() => {
    initAnalytics();
  }, []);
  // The account the last identify pass saw: useMe's profile trails an account
  // switch by one render, so that render still carries the previous account's.
  const identifiedOwnerRef = useRef<string | null>(null);
  useEffect(() => {
    // Wait for the Supabase session to restore: until then the user reads as
    // signed out, and a reset would throw away a returning person's identity.
    if (sessionLoading) return;
    const previousOwner = identifiedOwnerRef.current;
    identifiedOwnerRef.current = currentOwnerId;
    if (!currentOwnerId) {
      resetAnalytics();
      return;
    }
    if (previousOwner !== null && previousOwner !== currentOwnerId) return;
    // Identify once the profile is known: on a return visit that is at once,
    // from useMe's copy of the last /api/me answer (localStorage
    // `lorevok:me:<userId>`); otherwise when /api/me answers. A fresh answer that
    // changes the username or the admin flag re-runs this with the new values; a
    // failed /api/me keeps the remembered profile, and only with nothing
    // remembered is the person identified without a username (useMe then
    // settles as loaded with no profile). Without auth there is no /api/me.
    if (isAuthEnabled && !meLoaded) return;
    // A few profile properties so PostHog's persons are recognisable — never the
    // email (the Privacy Policy covers usage data only). Re-sent when they change.
    // Admins are marked for PostHog's "filter out internal and test users"; the
    // flag is never sent as false, so a person once marked stays marked.
    identifyUser(currentOwnerId, {
      set: {
        ui_language: uiLanguage,
        ...(currentUsername ? { username: currentUsername } : {}),
        ...(meLoaded && isAdmin ? { $internal_or_test_user: true } : {})
      },
      ...(userCreatedAt ? { setOnce: { signed_up_at: userCreatedAt } } : {})
    });
  }, [currentOwnerId, sessionLoading, meLoaded, isAdmin, currentUsername, uiLanguage, userCreatedAt]);
  const [selectedNovelId, setSelectedNovelId] = useState(
    (initialRoute.page === "detail" || initialRoute.page === "player") && initialRoute.novelId
      ? knownNovelId(initialRoute.novelId)
      : demoNovel.id
  );
  // A short link (/novel/<slug>-<code>, /n/<code>) being turned into the novel's
  // id: the detail/player page waits for it (behind its skeleton), because every
  // call it makes — views, likes, comments — is by id. It is read for reading:
  // the page then gets that same copy by id instead of reading the novel twice.
  const [resolvingLink, setResolvingLink] = useState<string | null>(() =>
    (initialRoute.page === "detail" || initialRoute.page === "player") &&
    initialRoute.novelId &&
    needsLinkResolution(initialRoute.novelId)
      ? initialRoute.novelId
      : null
  );
  useEffect(() => {
    if (!resolvingLink) return;
    let live = true;
    void resolveNovelLink(resolvingLink, (key) => loadNovelForReading(key)).then((id) => {
      if (!live) return;
      setSelectedNovelId(id);
      setResolvingLink(null);
    });
    return () => {
      live = false;
    };
  }, [resolvingLink]);
  const novelLinksVersion = useNovelLinksVersion();
  // Where the novel-detail page should return on "Back" — the page it was opened
  // from (catalog or landing). Deep links default to the catalog.
  const [detailFrom, setDetailFrom] = useState<Page>("catalog");
  // Where the player's exit button returns to: the page that opened it. Null on
  // a deep link / refresh straight into the player — exit then opens the
  // novel's detail page.
  const [playerFrom, setPlayerFrom] = useState<Page | null>(null);
  // How the player was reached, for its novel_opened event: the in-app page
  // that opened it, "ending" for «Read another story» on another novel's ending
  // panel, or null ("direct") for a link, a reload or the browser's
  // Back/Forward. Kept apart from playerFrom, which history navigation leaves
  // alone so the exit button still returns where the reader first came from.
  const [playerEntry, setPlayerEntry] = useState<string | null>(null);
  // The author whose public profile is open: the /u/ key — their username, or
  // the owner id for old links and profile-less owners. Seeded from a deep link.
  const [viewedAuthorId, setViewedAuthorId] = useState<string | null>(
    initialRoute.page === "author" ? initialRoute.novelId : null
  );
  // Both lists live here (not in the pages) because page components remount per
  // navigation — App-owned state means one fetch per identity instead of one
  // per visit. The landing hero features the first pinned novel present in the
  // catalog that this browser has not finished, else a feed novel in the UI
  // language's script — never picked by likes (lib/featuredNovels.ts
  // `pickLandingNovels`).
  const catalog = useNovelList(loadCatalogPage);
  const myNovels = useNovelList(loadMyNovelsPage);
  // The catalog's language / genre filters (lib/catalogFilters.ts), kept in its
  // address (/catalog?lang=ru&genre=horror) by the URL sync below and restored
  // by Back/Forward. The unfiltered list above stays the one the landing and
  // the novel suggestions read; a filtered catalog reads a list of its own.
  const [catalogFilters, setCatalogFilters] = useState<CatalogFilters>(() =>
    initialRoute.page === "catalog" && typeof window !== "undefined"
      ? parseCatalogFilters(window.location.search)
      : NO_CATALOG_FILTERS
  );
  const changeCatalogFilters = useCallback((next: CatalogFilters) => {
    setCatalogFilters((current) => (sameCatalogFilters(current, next) ? current : next));
  }, []);
  const loadFilteredCatalogPage = useCallback(
    (offset?: number, limit?: number) => loadCatalogPage(offset, limit, catalogFilters),
    [catalogFilters]
  );
  const filteredCatalog = useNovelList(loadFilteredCatalogPage);
  const [initialSceneId, setInitialSceneId] = useState<string | null>(null);
  const [currentJob, setCurrentJob] = useState<GenerationJob | null>(null);
  // A run remembered from the last visit is being re-attached (see the effect
  // below); the generation page shows its skeleton meanwhile, not "No
  // generation started".
  const [restoringJob, setRestoringJob] = useState(() => Boolean(safeLocalStorageGet(ACTIVE_JOB_KEY)));
  // Fire generation_completed / generation_failed once per run when a job reaches
  // a terminal state (the 5s polling loop would otherwise re-fire it repeatedly).
  // A retry runs the same job again, so a job seen running again is forgotten and
  // the retried run reports its own outcome.
  const firedTerminalRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!currentJob) return;
    if (currentJob.status === "running" || currentJob.status === "queued") {
      firedTerminalRef.current.delete(currentJob.id);
      return;
    }
    const terminal = currentJob.status === "completed" || currentJob.status === "failed";
    if (!terminal || firedTerminalRef.current.has(currentJob.id)) return;
    firedTerminalRef.current.add(currentJob.id);
    track(currentJob.status === "completed" ? "generation_completed" : "generation_failed", {
      job_id: currentJob.id,
      novel_id: currentJob.novel_id ?? null
    });
  }, [currentJob]);
  // The "generation started" popup: a time estimate + a novel to play while the
  // author's own novel generates. Set on submit, cleared when dismissed.
  const [genModal, setGenModal] = useState<{
    estimate: TranslationKey;
    suggested: NovelSummary | null;
  } | null>(null);
  // Terminal job the author has already seen on the status page — suppresses the
  // topbar generation pill so a finished run doesn't nag forever.
  const [acknowledgedJobId, setAcknowledgedJobId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingNovelId, setEditingNovelId] = useState<string | null>(
    initialRoute.page === "editor" ? initialRoute.novelId : null
  );
  // Scene the editor should select on open — set when jumping into the editor
  // from a specific scene (e.g. the player's "Edit" button), otherwise null so
  // the editor defaults to the start scene.
  const [editorInitialSceneId, setEditorInitialSceneId] = useState<string | null>(null);
  // Mirrors the editor's unsaved-changes flag. The editor reports it from an
  // effect on every change, and additionally clears it synchronously on the
  // paths that deliberately discard (delete, publish, continue) so the guard
  // below never double-prompts for a decision the editor already took.
  const editorDirtyRef = useRef(false);

  // Machine-translation follow-up: the author can ask (on the create form) for
  // the finished novel to be localized into other languages. That is a separate
  // job fired only once the generation job completes and its novel id is known.
  // `pendingTranslationsRef` remembers the requested languages keyed by the
  // generation job id (set at submit); `firedTranslationsRef` guards against
  // firing the translate job twice, since a job's completion is observed from
  // several places (the status page poll, the App-level background poll, and an
  // instant mock completion).
  const pendingTranslationsRef = useRef<Map<string, string[]>>(
    new Map(Object.entries(readPendingTranslations()))
  );
  const firedTranslationsRef = useRef<Set<string>>(new Set());

  // When a generation job finishes, kick off the requested machine-translation
  // job (fire-and-forget). Idempotent per job id, so it is safe to call from
  // every place that observes a job reaching "completed". Failures are
  // non-fatal — the novel already generated, and the author can re-run
  // translation from the editor.
  const maybeStartTranslation = useCallback((job: GenerationJob) => {
    // Paused: a request left in storage from before the pause is not fired.
    if (!NOVEL_TRANSLATION_ENABLED) return;
    if (job.status !== "completed" || !job.novel_id) return;
    if (firedTranslationsRef.current.has(job.id)) return;
    const languages = pendingTranslationsRef.current.get(job.id);
    if (!languages || languages.length === 0) return;
    firedTranslationsRef.current.add(job.id);
    pendingTranslationsRef.current.delete(job.id);
    writePendingTranslations(pendingTranslationsRef.current);
    void createTranslateJob(job.novel_id, languages).then(
      // The translate job reserved translate_novel credits.
      () => refreshCredits(),
      () => {
        // Non-fatal: translation can be re-run from the editor.
      }
    );
  }, [refreshCredits]);

  // When leaving a dirty editor, confirm the discard first. Returns false to
  // cancel the navigation. A no-op anywhere but the editor, or with no edits.
  const guardEditorLeave = useCallback((): boolean => {
    if (page !== "editor" || !editorDirtyRef.current) return true;
    const leave = window.confirm(t("editor.leaveUnsavedConfirm"));
    if (leave) editorDirtyRef.current = false;
    return leave;
  }, [page, t]);

  // Navigation used by the top bar and the editor's own back button — every
  // path out of the editor flows through the unsaved-changes guard.
  const guardedNavigate = useCallback(
    (next: Page) => {
      if (guardEditorLeave()) setPage(next);
    },
    [guardEditorLeave]
  );

  // Stable so the editor's dirty-reporting effect only fires on real changes.
  const handleEditorDirty = useCallback((dirty: boolean) => {
    editorDirtyRef.current = dirty;
  }, []);

  // Keep the address bar in sync with the current page. This only writes when
  // in-app navigation moved us somewhere the URL doesn't already reflect: after
  // a popstate (below) or the initial mount the path usually already matches, so
  // it no-ops — no duplicate history entries, idempotent under React StrictMode's
  // double-invoked effects. The very first write canonicalizes a non-canonical
  // entry URL (/catalog/, /nope, /edit) in place with replaceState so it leaves
  // no dead history entry; every later navigation pushes.
  //
  // A novel page is addressed by its short link key once it is known
  // (lib/novelLinks.ts); the key arriving after the page opened (an old id link)
  // upgrades the address in place — same screen, so no new history entry.
  const didSyncUrlRef = useRef(false);
  // Set by a redirect that must not leave its source in the history (Back would
  // only land on it and redirect again): the next write replaces the entry.
  const replaceNextUrlRef = useRef(false);
  const lastSyncedRef = useRef<{ page: Page; id: string | null; search: string } | null>(null);
  // The catalog's filters travel in its address, and only there: a filter change
  // is a new history entry (Back undoes it); no other page carries them.
  const catalogSearch = page === "catalog" ? catalogFilterSearch(catalogFilters) : "";
  useEffect(() => {
    if (typeof window === "undefined") return;
    // The short link is still being resolved: keep it in the address bar.
    if (resolvingLink && (page === "detail" || page === "player")) return;
    const idForPath =
      page === "editor"
        ? editingNovelId
        : page === "author"
          ? viewedAuthorId
          : page === "detail" || page === "player"
            ? selectedNovelId
            : null;
    const target = buildPath(
      page,
      idForPath && (page === "detail" || page === "player") ? novelLinkSegment(idForPath) : idForPath
    ) + catalogSearch;
    const current =
      window.location.pathname +
      (page === "catalog" ? catalogFilterSearch(parseCatalogFilters(window.location.search)) : "");
    if (current !== target) {
      const last = lastSyncedRef.current;
      const sameScreen = last !== null && last.page === page && last.id === idForPath && last.search === catalogSearch;
      const push = didSyncUrlRef.current && !sameScreen && !replaceNextUrlRef.current;
      // Rewriting the entry in place (a canonical path, a short link's upgrade, a
      // redirect) keeps the landing's campaign tags: analytics reads them from the
      // address bar, and without them the visit counts as "direct".
      if (push) window.history.pushState(null, "", target);
      else window.history.replaceState(null, "", withSearch(target, campaignSearch(window.location.search)));
    }
    replaceNextUrlRef.current = false;
    didSyncUrlRef.current = true;
    lastSyncedRef.current = { page, id: idForPath, search: catalogSearch };
  }, [page, selectedNovelId, editingNovelId, viewedAuthorId, resolvingLink, novelLinksVersion, catalogSearch]);

  // The manual $pageview. Declared after the URL sync above so PostHog reads the
  // screen's own address, not the previous one. One per screen: another novel
  // (or author) on the same kind of page is a new screen, while the address
  // upgrading to a novel's short link is not. A short link being resolved waits:
  // its page is not on screen yet, and its id is not known.
  const pageviewNovelId =
    page === "editor"
      ? editingNovelId
      : page === "detail" || page === "player"
        ? selectedNovelId
        : null;
  const pageviewScreenId = page === "author" ? viewedAuthorId : pageviewNovelId;
  const lastPageviewRef = useRef<{ page: Page; id: string | null } | null>(null);
  useEffect(() => {
    if (resolvingLink && (page === "detail" || page === "player")) return;
    const last = lastPageviewRef.current;
    if (last && last.page === page && last.id === pageviewScreenId) return;
    lastPageviewRef.current = { page, id: pageviewScreenId };
    trackPageView(page, pageviewNovelId ? { novel_id: pageviewNovelId } : undefined);
  }, [page, pageviewScreenId, pageviewNovelId, resolvingLink]);

  // A reader sent to sign up from a novel (Like, comment) goes back to that
  // novel's page once signed in. Held while the auth page is on screen (and
  // while the reader checks the Terms or Privacy it links to); a sign-up that waits for its confirmation email also stores it
  // (lib/authReturn.ts), because that link opens /signin later, often in
  // another tab.
  const [authReturn, setAuthReturn] = useState<{ reason: ReaderPrompt; novelId: string } | null>(null);
  const authReturnRef = useRef(authReturn);
  authReturnRef.current = authReturn;
  useEffect(() => {
    if (page !== "auth" && !isLegalPage(page)) setAuthReturn(null);
  }, [page]);

  // An account-only action (Like, comment) by a signed-out reader: the sign-up
  // form, speaking to readers, and back to this novel afterwards.
  function askToSignUp(reason: ReaderPrompt, surface: "detail" | "ending", novelId: string) {
    track("signup_prompt_shown", { reason, surface, novel_id: novelId });
    setAuthReturn({ reason, novelId });
    setPage("auth");
  }

  // Where a sign-in lands: the novel the reader came from, else the catalog.
  // A visitor who pressed "Continue with Google / Discord" on the sign-in gate
  // of an owner page (Create, My Novels, the editor) goes back to that page: the
  // round trip loses it, as a password sign-in on the same gate never would.
  const goAfterAuth = useCallback(() => {
    const stored = takeAuthReturn();
    const novelId = authReturnRef.current?.novelId ?? stored;
    const gated = takeOAuthReturnTarget();
    setAuthReturn(null);
    if (novelId) {
      setSelectedNovelId(novelId);
      setDetailFrom("catalog");
      setPage("detail");
    } else if (gated) {
      if (gated.page === "editor" && gated.novelId) {
        setEditingNovelId(gated.novelId);
        setEditorInitialSceneId(null);
      }
      setPage(gated.page);
    } else {
      setPage("catalog");
    }
  }, []);

  // A signed-in account that never chose its username — a first sign-in with
  // Google or Discord, or an older account handed author<digits> — finishes
  // signing up before anything else (FinishSignUpPage, rendered in place of the
  // app below). The screen goes the moment the claim succeeds; /api/me catches
  // up behind it. A password-reset link goes first: its new-password form is
  // what the visitor came for, and this screen follows it.
  const [signUpFinishedFor, setSignUpFinishedFor] = useState<string | null>(null);
  const needsUsername =
    isAuthEnabled &&
    Boolean(user) &&
    meLoaded &&
    !usernameConfirmed &&
    !recoveringPassword &&
    signUpFinishedFor !== user?.id;
  // Sign-in with Google / Discord comes back as a new page load, so the provider
  // was remembered before leaving (lib/oauthReturn.ts). Reported once the
  // account is known: an account with its username signed in; one without is
  // finishing its sign-up, reported when that is done.
  // Kept with the account it was read for: signing out from the finish screen
  // (or into another account) must not hand the provider to whoever finishes next.
  const oauthSignInRef = useRef<{ userId: string; provider: OAuthProvider } | null>(null);
  useEffect(() => {
    if (!isAuthEnabled || !user || !meLoaded) return;
    const provider = takeOAuthStart();
    if (!provider) return;
    if (usernameConfirmed) track("login_completed", { method: provider });
    else oauthSignInRef.current = { userId: user.id, provider };
  }, [user, meLoaded, usernameConfirmed]);
  const finishSignUp = useCallback(
    ({ claimed }: { claimed: boolean }) => {
      if (!user) return;
      // Only the tab that claimed the username reports the sign-up: a claim that
      // found it already set was finished (and counted) in another tab.
      if (claimed) {
        // A new Google / Discord account's sign-up ends here; an email account
        // (an invite that got author<digits>) only signed in, if it came that way.
        const createdWith = accountOAuthProvider(user);
        const signedInWith = oauthSignInRef.current?.userId === user.id ? oauthSignInRef.current.provider : null;
        if (createdWith) track("sign_up_completed", { method: createdWith, needs_confirmation: false });
        else if (signedInWith) track("login_completed", { method: signedInWith });
      }
      oauthSignInRef.current = null;
      setSignUpFinishedFor(user.id);
      refreshMe();
    },
    [user, refreshMe]
  );

  // A signed-in visitor has nothing to do on the sign-in page — it is reached by
  // a typed or remembered /signin, Back, or the e-mail confirmation link (which
  // lands on /signin already signed in). Send them on in place: to the novel
  // they signed up from, else the catalog. A password-reset link is the
  // exception: its session renders the new-password form above every page,
  // whatever `page` says.
  useEffect(() => {
    if (!isAuthEnabled || sessionLoading || !user || page !== "auth" || recoveringPassword) return;
    replaceNextUrlRef.current = true;
    goAfterAuth();
  }, [page, user, sessionLoading, recoveringPassword, goAfterAuth]);

  // Back/forward drives React state from the URL. The id holder the target page
  // reads is set first so a deep-linked detail/player/editor opens the right
  // novel; opening the player also clears any stale editor-picked scene so it
  // resumes from saved progress. Leaving a dirty editor via browser Back/Forward
  // runs the SAME unsaved-changes guard the in-app buttons do — a same-document
  // popstate never triggers the editor's beforeunload prompt, so without this
  // Back would silently discard unsaved edits. If the author cancels, the editor
  // URL is restored (undoing the browser's navigation) and no state changes.
  //
  // The listener is registered ONCE and reads what it needs through refs, so it
  // never re-subscribes on unrelated re-renders (guardEditorLeave is a fresh
  // reference every render because useT() is) yet always sees current state.
  const pageRef = useRef(page);
  pageRef.current = page;
  const editingNovelIdRef = useRef(editingNovelId);
  editingNovelIdRef.current = editingNovelId;
  const guardEditorLeaveRef = useRef(guardEditorLeave);
  guardEditorLeaveRef.current = guardEditorLeave;
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      const route = parseRoute(window.location.pathname);
      if (
        pageRef.current === "editor" &&
        route.page !== "editor" &&
        !guardEditorLeaveRef.current()
      ) {
        window.history.pushState(null, "", buildPath("editor", editingNovelIdRef.current));
        return;
      }
      if (route.novelId) {
        if (route.page === "editor") setEditingNovelId(route.novelId);
        else if (route.page === "author") setViewedAuthorId(route.novelId);
        else if (route.page === "detail" || route.page === "player") {
          if (needsLinkResolution(route.novelId)) setResolvingLink(route.novelId);
          else setSelectedNovelId(knownNovelId(route.novelId));
        }
      }
      // History-driven navigation carries no jump-in scene — clear both so the
      // editor/player don't reopen at a scene from an earlier in-app jump. Nor
      // is it an in-app open: the player reports it as a direct entry.
      if (route.page === "editor") setEditorInitialSceneId(null);
      if (route.page === "player") {
        setInitialSceneId(null);
        setPlayerEntry(null);
      }
      // The catalog's filters come back with its address.
      if (route.page === "catalog") {
        const filters = parseCatalogFilters(window.location.search);
        setCatalogFilters((current) => (sameCatalogFilters(current, filters) ? current : filters));
      }
      setPage(route.page);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Re-attach to a generation job that was still running when the page was last
  // closed. The pipeline keeps running on the server regardless of the browser.
  useEffect(() => {
    const activeJobId = safeLocalStorageGet(ACTIVE_JOB_KEY);
    // No remembered run: restoringJob started false from the same read.
    if (!activeJobId) {
      return;
    }

    let cancelled = false;
    let retryTimer: number | undefined;
    const attach = () => {
      void loadGenerationJob(activeJobId).then(
        (job) => {
          if (cancelled) return;
          setRestoringJob(false);
          setCurrentJob(job);
          // Resume watching the run — but only take over the screen on a plain
          // landing-page load. A deep-linked refresh (e.g. /catalog, /edit/:id)
          // keeps its page; the topbar generation pill keeps tracking the job there.
          if (initialRoute.page === "landing") setPage("status");
          if (job.status !== "running" && job.status !== "queued") {
            safeLocalStorageRemove(ACTIVE_JOB_KEY);
          }
        },
        (error) => {
          if (cancelled) return;
          setRestoringJob(false);
          if (isGoneJobError(error)) {
            safeLocalStorageRemove(ACTIVE_JOB_KEY);
            return;
          }
          // The API is briefly unreachable (a deploy restarts it while the
          // pipeline keeps running): keep the job and try again shortly.
          retryTimer = window.setTimeout(attach, JOB_RETRY_MS);
        }
      );
    };
    attach();

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
    // initialRoute is a mount-stable ref value, so this still runs exactly once.
  }, [initialRoute]);

  // Load both lists once the session has resolved, and re-load them whenever
  // the signed-in user changes so ownership-dependent views reflect the
  // current account. Waiting for the session avoids a discarded pre-auth
  // request pair at boot; identity changes re-arm the skeletons.
  // The refresh functions are useCallback-stable, so these effects re-run on
  // identity changes only — not on every data update of the wrapper objects.
  const { refresh: refreshCatalog } = catalog;
  const { refresh: refreshMyNovels } = myNovels;
  // "My Novels" is only shown on the Profile/Editor pages, so it is loaded
  // lazily the first time one of those opens (per identity) instead of eagerly
  // on boot — the common landing/catalog/play path then makes one fewer backend
  // request, freeing the (single-instance) server for the catalog read. This ref
  // records the identity My Novels was last loaded for.
  const ownerIdentity = user?.id ?? null;
  const myNovelsLoadedForRef = useRef<string | null | undefined>(undefined);

  // Boot / identity change: load the public catalog (the landing hero and the
  // catalog page both need it). My Novels is deferred to the effect below.
  useEffect(() => {
    if (sessionLoading) return;
    // Signed out: the catalog is public only in open-signup mode. In the closed
    // beta every read is gated to 401, so fetching would only spam the console
    // with failed requests — and until the site config arrives we don't know.
    if (isAuthEnabled && !ownerIdentity && !openSignup) return;
    void refreshCatalog();
  }, [ownerIdentity, sessionLoading, refreshCatalog, openSignup]);

  // Lazily load My Novels the first time an owner page is shown for the current
  // identity (or when the identity changes while already on one).
  useEffect(() => {
    if (sessionLoading) return;
    if (isAuthEnabled && !ownerIdentity) return;
    if (page !== "profile" && page !== "editor") return;
    if (myNovelsLoadedForRef.current === ownerIdentity) return;
    myNovelsLoadedForRef.current = ownerIdentity;
    void refreshMyNovels();
  }, [page, ownerIdentity, sessionLoading, refreshMyNovels]);

  // A filtered catalog loads its list when the catalog page shows it — once per
  // filter set and identity. A content change marks it "stale|<key>", and it is
  // then refreshed in place (silently) the next time it is shown.
  const catalogFiltered = hasCatalogFilters(catalogFilters);
  const filteredCatalogKey = catalogFiltered ? `${ownerIdentity ?? ""}|${catalogFilterSearch(catalogFilters)}` : null;
  const [filteredCatalogFor, setFilteredCatalogFor] = useState<string | null>(null);
  const { refresh: refreshFilteredCatalog } = filteredCatalog;
  useEffect(() => {
    if (sessionLoading || page !== "catalog" || filteredCatalogKey === null) return;
    if (filteredCatalogFor === filteredCatalogKey) return;
    if (isAuthEnabled && !ownerIdentity && !openSignup) return;
    const silent = filteredCatalogFor === `stale|${filteredCatalogKey}`;
    setFilteredCatalogFor(filteredCatalogKey);
    void refreshFilteredCatalog({ silent });
  }, [page, filteredCatalogKey, filteredCatalogFor, sessionLoading, ownerIdentity, openSignup, refreshFilteredCatalog]);
  const filteredCatalogShown =
    filteredCatalogKey !== null &&
    (filteredCatalogFor === filteredCatalogKey || filteredCatalogFor === `stale|${filteredCatalogKey}`);

  // Content mutations (save/publish/delete/generation) change both lists.
  // Refresh the catalog always; refresh My Novels only once it has been loaded
  // for this identity — otherwise there is nothing on screen to update, and the
  // lazy loader above fetches a fresh copy when an owner page is next opened.
  const refreshNovelLists = useCallback(() => {
    const tasks = [refreshCatalog({ silent: true })];
    if (myNovelsLoadedForRef.current === ownerIdentity) {
      tasks.push(refreshMyNovels({ silent: true }));
    }
    setFilteredCatalogFor((current) => (current && !current.startsWith("stale|") ? `stale|${current}` : current));
    return Promise.all(tasks);
  }, [refreshCatalog, refreshMyNovels, ownerIdentity]);

  // Back from maintenance (a redeploy): the lists may have missed updates.
  useEffect(() => onApiBack(() => void refreshNovelLists()), [refreshNovelLists]);

  // A running job's polling normally lives on the status page; when the user
  // navigates away mid-run, keep a slow App-level poll so the finished draft
  // still lands in the lists — the pipeline keeps running server-side
  // regardless of what the browser shows. Deps are the job's id/status (not
  // the object): a tick that only refreshes the payload must not tear the
  // interval down and re-render the whole tree.
  const currentJobId = currentJob?.id;
  const currentJobStatus = currentJob?.status;

  // Re-read the balance on each status change of the tracked job, wherever the
  // change came from (the poll below, the status page's own poll, Retry, Cancel).
  useRefreshOnJobStatus(currentJobId, currentJobStatus, refreshCredits);
  useEffect(() => {
    if (page === "status" || !currentJobId) return;
    if (currentJobStatus !== "running" && currentJobStatus !== "queued") return;
    // `active` drops responses that land after this effect is torn down (a new
    // job was submitted, the user opened the status page) — a stale job's
    // terminal response must not overwrite the new job or delete its
    // ACTIVE_JOB_KEY. `inFlight` keeps slow responses from overlapping and
    // resolving out of order.
    let active = true;
    let inFlight = false;
    let consecutiveGone = 0;
    const timer = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      loadGenerationJob(currentJobId).then(
        (job) => {
          inFlight = false;
          consecutiveGone = 0;
          if (!active || job.id !== currentJobId) return;
          // Skip no-op updates — a fresh-but-identical payload every 5s would
          // re-render the whole tree for nothing.
          setCurrentJob((current) =>
            current &&
            current.id === job.id &&
            current.status === job.status &&
            current.current_step === job.current_step &&
            current.progress === job.progress
              ? current
              : job
          );
          if (job.status !== "running" && job.status !== "queued") {
            safeLocalStorageRemove(ACTIVE_JOB_KEY);
            if (job.status === "completed") {
              maybeStartTranslation(job);
              void refreshNovelLists();
            }
          }
        },
        (error) => {
          inFlight = false;
          // Only a job that is really gone (pruned: 404, not ours: 403) stops
          // the tracking. A deploy makes the API unreachable for a minute or
          // more while the pipeline itself resumes on the new server, so
          // network errors / 5xx / timeouts just wait for the next tick —
          // giving up on them is how the pill used to vanish mid-generation.
          if (!isGoneJobError(error)) return;
          consecutiveGone += 1;
          if (active && consecutiveGone >= 2) {
            window.clearInterval(timer);
            safeLocalStorageRemove(ACTIVE_JOB_KEY);
          }
        }
      );
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [page, currentJobId, currentJobStatus, refreshNovelLists, maybeStartTranslation]);

  // Once the author lands on the status page for a finished (terminal) job,
  // remember it so the topbar pill stops showing — they've seen the result.
  // A still-running job is never acknowledged, so its pill returns whenever they
  // navigate away to keep tracking it.
  useEffect(() => {
    if (
      page === "status" &&
      currentJob &&
      currentJob.status !== "running" &&
      currentJob.status !== "queued"
    ) {
      setAcknowledgedJobId(currentJob.id);
    }
  }, [page, currentJob]);

  // The pill tracks the author's generation from anywhere except the status page
  // itself (redundant there) and once a finished run has been acknowledged.
  const pillJob =
    currentJob && page !== "status" && currentJob.id !== acknowledgedJobId ? currentJob : null;

  async function submitGeneration(
    input: {
      idea: string;
      genre?: string;
      tone?: string;
      length?: "short" | "medium" | "long";
      artStyle?: string;
      language?: string;
      detectLanguage?: boolean;
      generateImages?: boolean;
      emotions?: boolean;
      translateLanguages?: string[];
    },
    // Callers on the Create page show the error inline via `createError`. The
    // editor's rebuild flow instead awaits this call and surfaces the error in
    // its own UI, so it needs the failure to propagate rather than be swallowed.
    // `source` tells the two apart in analytics: both start a new novel.
    options: { rethrow?: boolean; source?: "create" | "rebuild" } = {}
  ) {
    setCreateError(null);
    const source = options.source ?? "create";

    // The requested translation languages are not part of the generation job
    // itself — they seed a separate translate job fired when this one completes.
    const { translateLanguages, ...genInput } = input;

    try {
      const job = await createGenerationJob(genInput).catch((error: unknown) => {
        track("generation_start_failed", { reason: startFailureReason(error), source });
        throw error;
      });
      // Funnel step 1 (and, on a repeat, "generated another"). PostHog counts
      // the 2nd occurrence per person for the activation funnel.
      track("generation_started", {
        job_id: job.id,
        kind: "new",
        source,
        length: input.length,
        with_images: input.generateImages ?? true,
        with_emotions: input.emotions === true && input.generateImages !== false
      });
      // Remember what to translate this job's novel into once it finishes.
      if (translateLanguages && translateLanguages.length > 0) {
        pendingTranslationsRef.current.set(job.id, translateLanguages);
        writePendingTranslations(pendingTranslationsRef.current);
      }
      // The job reserved generate_novel credits; reflect the new balance.
      refreshCredits();
      setCurrentJob(job);
      setPage("status");
      if (job.status === "completed") {
        safeLocalStorageRemove(ACTIVE_JOB_KEY);
        maybeStartTranslation(job);
        await refreshNovelLists();
      } else {
        safeLocalStorageSet(ACTIVE_JOB_KEY, job.id);
        // Only offer the "play while you wait" popup for a run that will
        // actually take time (not one that failed instantly, e.g. no credits).
        if (job.status === "running" || job.status === "queued") {
          setGenModal({
            estimate: estimateGenerationTimeKey(input.length, input.generateImages ?? true),
            suggested: pickSuggestedNovel(catalog.novels, isNovelFinished)
          });
        }
      }
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : t("create.requestFailed"));
      if (options.rethrow) throw error;
    }
  }

  // Continue ("догенерация") an existing novel past one of its endings. Reuses
  // the whole generation-status flow: the returned job's novel_id is the same
  // novel, so the status page's "Edit Draft" reopens it once it has grown.
  // Throws on failure so the editor can surface the error inline.
  async function submitExtension(novelId: string, fromEndingId: string, guidance?: string) {
    const job = await extendNovel(novelId, { fromEndingId, guidance });
    // A continuation is a generation too — keep generation_started/_completed
    // symmetric. The activation funnel filters on kind:"new".
    track("generation_started", { job_id: job.id, kind: "extend", novel_id: novelId });
    // The continuation reserved extend_novel credits; reflect the new balance.
    refreshCredits();
    setCurrentJob(job);
    // Only the editor starts a continuation, and it confirms the discard itself
    // before calling in. Clear the mirrored flag so leaving for the status page
    // doesn't prompt a second time for the same decision.
    editorDirtyRef.current = false;
    setPage("status");
    if (job.status === "completed") {
      safeLocalStorageRemove(ACTIVE_JOB_KEY);
      await refreshNovelLists();
    } else {
      safeLocalStorageSet(ACTIVE_JOB_KEY, job.id);
    }
  }

  // Rebuild an existing novel from its original idea. Reuses the whole
  // create-and-watch flow: a fresh generation job on the stored idea (surfaced
  // by the AI editor's planner) produces a NEW novel — the current draft is left
  // intact — and the app follows the job to the generation status page.
  async function submitRegeneration(seed: {
    idea: string;
    language: string;
    tone: string;
    genre?: string;
    length?: "short" | "medium" | "long";
  }) {
    // Let the failure propagate so the editor can reset its button and show the
    // error; on success submitGeneration has already navigated to the status
    // page, so only THEN clear the mirrored dirty flag (a failed run stays in the
    // editor and must keep its real dirty state, or the leave-guard would stop
    // warning about unsaved edits).
    await submitGeneration(
      {
        idea: seed.idea,
        language: seed.language,
        tone: seed.tone,
        genre: seed.genre,
        length: seed.length
      },
      { rethrow: true, source: "rebuild" }
    );
    editorDirtyRef.current = false;
  }

  // The player's exit: back to the page that opened it, or — on a deep link or a
  // refresh straight into the player — to the novel's detail page.
  function exitPlayer() {
    if (playerFrom) {
      setPage(playerFrom);
    } else {
      setDetailFrom("catalog");
      setPage("detail");
    }
  }

  // The one navigation invariant worth a helper: opening the player must
  // reset initialSceneId, or it reopens at a stale scene from a previous
  // editing session. Only EditorPage passes an explicit scene.
  function openPlayer(novelId: string, sceneId: string | null = null) {
    // Opening the player from a dirty editor would lose the unsaved edits (the
    // player reloads from the server), so it goes through the same guard.
    if (!guardEditorLeave()) return;
    setPlayerFrom(page === "player" ? null : page);
    setPlayerEntry(page);
    setSelectedNovelId(novelId);
    setInitialSceneId(sceneId);
    setPage("player");
  }

  function openEditor(novelId: string, sceneId: string | null = null) {
    setEditingNovelId(novelId);
    setEditorInitialSceneId(sceneId);
    setPage("editor");
  }

  // Open an author's public profile at /u/<username> (or /u/<id> for an owner
  // without a profile). Guarded like the player: leaving a dirty editor via an
  // author byline must run the same unsaved-changes check.
  function openAuthor(ownerId: string, username?: string) {
    if (!guardEditorLeave()) return;
    setViewedAuthorId(authorRouteKey(ownerId, username));
    setPage("author");
  }

  // With auth configured, owner actions (create/edit/publish/My Novels) require
  // a signed-in user; anonymous callers get the sign-in screen instead of raw
  // 401s. Browsing and playing stay public. No-op when auth isn't configured.
  const authGateActive = isOwnerPageBlocked(page, {
    authEnabled: isAuthEnabled,
    signedIn: Boolean(user),
    sessionLoading
  });

  // The head for search engines and the browser tab (lib/pageMeta.ts): the
  // route's title, description, canonical and robots; the novel and author
  // pages refine theirs once loaded. A screen standing in for the route (the
  // sign-in gate, the closed beta, a blocked account, a new password) is
  // private, and so is an address the router does not know (it shows the
  // landing page) until the visitor moves on.
  const [unknownEntryPath, setUnknownEntryPath] = useState(
    () => typeof window !== "undefined" && !isKnownPath(window.location.pathname)
  );
  useEffect(() => {
    if (page !== "landing") setUnknownEntryPath(false);
  }, [page]);
  const metaPage: Page =
    recoveringPassword || authGateActive || (isAuthEnabled && user && betaBlocked) || (fullyClosed && !isLegalPage(page))
      ? "auth"
      : page;
  usePageMeta(
    routePageMeta(metaPage, t, {
      novelSegment: resolvingLink ?? novelLinkSegment(selectedNovelId),
      authorKey: viewedAuthorId,
      unknownPath: unknownEntryPath
    }),
    "route"
  );

  // Closed beta: no shell until signed in — the whole app is the sign-in screen.
  // The legal documents stay reachable before sign-in.
  if (fullyClosed) {
    return (
      <div className="app-shell">
        <main>
          {isLegalPage(page) ? (
            <LegalDocumentPage documentId={page} onNavigate={setPage} />
          ) : (
            <AuthPage onAuthenticated={() => undefined} onNavigate={setPage} />
          )}
        </main>
        <SiteFooter onNavigate={setPage} />
        <CookieBanner onNavigate={setPage} />
      </div>
    );
  }

  // Signed in, but the account is not on the closed-beta allowlist. Explain it
  // and offer a manual sign-out instead of silently bouncing to the login page.
  if (isAuthEnabled && user && betaBlocked) {
    return (
      <div className="app-shell">
        <main>
          <AccountBlockedScreen
            reason={betaBlocked}
            email={user.email ?? ""}
            onSignOut={async () => {
              await signOut();
              setBetaBlocked(null);
            }}
          />
        </main>
        <SiteFooter onNavigate={setPage} />
      </div>
    );
  }

  // Signed in without a username of its own yet: "Finish signing up" stands in
  // for every page but the legal documents (its consent links to them).
  if (needsUsername && user) {
    return (
      <div className="app-shell">
        <main>
          {isLegalPage(page) ? (
            <LegalDocumentPage documentId={page} onNavigate={setPage} />
          ) : (
            <FinishSignUpPage
              email={user.email ?? ""}
              currentUsername={currentUsername}
              onDone={finishSignUp}
              onSignOut={async () => {
                await signOut();
                setPage("landing");
              }}
              onDeleteAccount={async () => {
                await deleteAccount();
                await signOut();
                setPage("landing");
              }}
            />
          )}
        </main>
        <SiteFooter onNavigate={setPage} />
        <CookieBanner onNavigate={setPage} />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Topbar
        activePage={page}
        onNavigate={guardedNavigate}
        authEnabled={isAuthEnabled}
        userEmail={user?.email ?? null}
        sessionLoading={sessionLoading}
        identityLoaded={meLoaded}
        userDisplayName={userDisplayName}
        userAvatarUrl={userAvatarUrl}
        balanceCents={credits.balanceCents}
        balancePending={credits.balanceLoading}
        isAdmin={isAdmin}
        generationStatus={pillJob?.status ?? null}
        generationProgress={pillJob?.progress ?? 0}
        onOpenGeneration={() => guardedNavigate("status")}
        onOpenProfile={currentOwnerId ? () => openAuthor(currentOwnerId, currentUsername) : undefined}
        showNotifications={!isAuthEnabled || Boolean(user)}
        onOpenNovel={(novelId) => {
          setSelectedNovelId(novelId);
          setDetailFrom(page);
          setPage("detail");
        }}
        onSignIn={() => setPage("auth")}
        onSignOut={async () => {
          // Signing out leaves the editor like any other navigation — unsaved
          // edits must not vanish just because this control sits in the topbar.
          if (!guardEditorLeave()) return;
          await signOut();
          setPage("landing");
        }}
      />

      <main>
        {/* While a lazy page's code downloads, its own skeleton stands in, so
            the page lands on the same shape instead of after a "Loading…" line. */}
        <Suspense
          fallback={
            <RouteFallback
              page={page}
              statusHasJob={currentJob !== null || restoringJob}
              onExitPlayer={exitPlayer}
            />
          }
        >
        {recoveringPassword ? (
          <NewPasswordPage
            onDone={() => {
              setRecoveringPassword(false);
              setPage("catalog");
            }}
          />
        ) : authGateActive ? (
          // A signed-out visitor reaching an owner page (e.g. "Create") is most
          // likely new — lead with the sign-up form when signup is open.
          siteConfig === null ? (
            <AuthPageSkeleton />
          ) : (
            <AuthPage
              key={String(openSignup)}
              onAuthenticated={() => undefined}
              onNavigate={guardedNavigate}
              openSignup={openSignup}
              initialMode="signup"
              oauthProviders={siteConfig.oauthProviders}
              // Google / Discord return to /signin: keep the page the gate stands on.
              onOAuthStart={() => rememberOAuthReturnTarget(gatedPageTarget(page, editingNovelId))}
            />
          )
        ) : (
        <>
        {page === "landing" && (
          <LandingPage
            catalogNovels={catalog.novels}
            catalogLoaded={catalog.loaded}
            onNavigate={setPage}
            onPlay={openPlayer}
            onOpenDetail={(novelId) => {
              setSelectedNovelId(novelId);
              setDetailFrom("landing");
              setPage("detail");
            }}
            onOpenAuthor={openAuthor}
          />
        )}
        {page === "auth" &&
          (siteConfig === null ? (
            <AuthPageSkeleton />
          ) : (
            <AuthPage
              key={`${openSignup}-${authReturn?.reason ?? ""}`}
              // The session change may have moved on already (the effect above);
              // the form's own callback must not send the visitor on twice.
              onAuthenticated={() => {
                if (pageRef.current === "auth") goAfterAuth();
              }}
              onNavigate={guardedNavigate}
              openSignup={openSignup}
              initialMode={authReturn ? "signup" : "signin"}
              readerPrompt={authReturn?.reason}
              onSignUpPending={() => {
                if (authReturn) rememberAuthReturn(authReturn.novelId);
              }}
              oauthProviders={siteConfig.oauthProviders}
              // Google / Discord return on a new page load: keep the novel for it too.
              onOAuthStart={() => {
                if (authReturn) rememberAuthReturn(authReturn.novelId);
                // Not from an owner page's gate: an abandoned start from one must not send this one there.
                rememberOAuthReturnTarget(null);
              }}
            />
          ))}
        {isLegalPage(page) && <LegalDocumentPage documentId={page} onNavigate={guardedNavigate} />}
        {page === "pricing" && (
          <PricingPage
            signedIn={!isAuthEnabled || Boolean(user)}
            userEmail={user?.email ?? null}
            balanceCents={credits.balanceCents}
            balanceLoading={credits.balanceLoading}
            onNavigate={guardedNavigate}
            onSignIn={() => setPage("auth")}
            onPurchased={refreshCredits}
          />
        )}
        {import.meta.env.DEV && page === "showcase" && (
          <ShowcasePage onBack={() => setPage("landing")} />
        )}
        {/* Dev-only: the real generation page on a continuous mock loop. Rendered
            bare in <main> (like the real `status` route) so it matches 1:1. */}
        {import.meta.env.DEV && page === "genloop" && <GenerationLoopPage />}
        {page === "catalog" && (
          <CatalogPage
            pagination={catalogFiltered ? filteredCatalog : catalog}
            novels={catalogFiltered ? filteredCatalog.novels : catalog.novels}
            source={catalogFiltered ? filteredCatalog.source : catalog.source}
            loading={catalogFiltered ? !filteredCatalog.loaded || !filteredCatalogShown : !catalog.loaded}
            filters={catalogFilters}
            onFiltersChange={changeCatalogFilters}
            // The filter bar reads under the catalog's own rule: a signed-out
            // read waits for an open site mode (a closed one answers 403, and
            // that would flag a visitor as "not in the beta").
            facetsReady={!sessionLoading && !(isAuthEnabled && !ownerIdentity && !openSignup)}
            facetsReloadKey={apiEpoch}
            onRetry={() => void refreshFilteredCatalog()}
            onNavigate={guardedNavigate}
            onPlay={openPlayer}
            onOpenDetail={(novelId) => {
              setSelectedNovelId(novelId);
              setDetailFrom("catalog");
              setPage("detail");
            }}
            onOpenAuthor={openAuthor}
            onCreate={() => setPage("create")}
          />
        )}
        {resolvingLink && (page === "detail" || page === "player") && (
          <RouteFallback page={page} statusHasJob={false} onExitPlayer={exitPlayer} />
        )}
        {page === "detail" && !resolvingLink && (
          <NovelDetailPage
            novelId={selectedNovelId}
            currentOwnerId={currentOwnerId}
            onRequireAccount={(reason) => askToSignUp(reason, "detail", selectedNovelId)}
            onPlay={openPlayer}
            onEdit={openEditor}
            onOpenAuthor={openAuthor}
            onOpenDetail={(novelId) => {
              setSelectedNovelId(novelId);
              setPage("detail");
            }}
            onPublished={(published) => {
              track("novel_published", { novel_id: published.id });
              // Flip the badge in the My Novels list immediately, then reconcile.
              myNovels.update((current) =>
                current.map((novel) =>
                  novel.id === published.id ? { ...novel, visibility: published.visibility } : novel
                )
              );
              void refreshNovelLists();
            }}
            onBack={() => setPage(detailFrom)}
          />
        )}
        {page === "author" && (
          <AuthorPage
            authorId={viewedAuthorId}
            currentOwnerId={currentOwnerId}
            onProfileChanged={refreshMe}
            onCreditsSpent={refreshCredits}
            onPlay={openPlayer}
            onOpenDetail={(novelId) => {
              setSelectedNovelId(novelId);
              setDetailFrom("author");
              setPage("detail");
            }}
            onCreate={() => setPage("create")}
            onBack={() => setPage("catalog")}
            accountEmail={isAuthEnabled ? user?.email ?? null : null}
            avatarCostCredits={operationCredits(credits.prices, "generate_avatar")}
            onAccountDeleted={async () => {
              await signOut();
              setPage("landing");
            }}
          />
        )}
        {page === "player" && !resolvingLink && (
          <PlayerPage
            novelId={selectedNovelId}
            initialSceneId={initialSceneId}
            currentOwnerId={currentOwnerId}
            signedOut={isAuthEnabled && !user}
            // How the reader got here, for the player's novel_opened event.
            entry={playerEntry ?? "direct"}
            catalogNovels={catalog.novels}
            onPlayNext={(novelId) => {
              openPlayer(novelId);
              setPlayerEntry("ending");
            }}
            onRequireAccount={(reason) => askToSignUp(reason, "ending", selectedNovelId)}
            onCreate={() => setPage("create")}
            onEdit={openEditor}
            onEngagementChanged={() => void refreshNovelLists()}
            onExit={exitPlayer}
            onOpenDetail={(novelId) => {
              setSelectedNovelId(novelId);
              // Back from the page goes where the reader came from (as exitPlayer does).
              if (playerFrom !== "detail") setDetailFrom("catalog");
              setPage("detail");
            }}
          />
        )}
        {page === "create" && (
          <CreatePage
            error={createError}
            prices={credits.prices}
            onSubmit={(input) => submitGeneration(input)}
            onCreditsSpent={refreshCredits}
          />
        )}
        {page === "status" && (
          <GenerationStatusPage
            job={currentJob}
            restoring={restoringJob}
            onJobUpdate={(job) => {
              setCurrentJob(job);
              if (job.status !== "running" && job.status !== "queued") {
                safeLocalStorageRemove(ACTIVE_JOB_KEY);
              }
              if (job.status === "completed") {
                // Primary completion hook: the author is on the status page
                // watching this poll. Fire the requested translation once.
                maybeStartTranslation(job);
                void refreshNovelLists();
              }
            }}
            onEdit={openEditor}
            onPlay={openPlayer}
            onPublished={() => {
              void refreshNovelLists();
              setPage("catalog");
            }}
          />
        )}
        {page === "editor" && (
          <EditorPage
            novelId={editingNovelId}
            initialSceneId={editorInitialSceneId}
            currentOwnerId={currentOwnerId}
            prices={credits.prices}
            onCreditsSpent={refreshCredits}
            onNavigate={guardedNavigate}
            onPlay={openPlayer}
            onExtend={submitExtension}
            onRegenerate={submitRegeneration}
            onDirtyChange={handleEditorDirty}
            onLibraryChanged={(change) => {
              if (change?.deletedNovelId) {
                // Filter synchronously — the user lands on My Novels right
                // after the delete, before the background refetch resolves.
                const deletedId = change.deletedNovelId;
                myNovels.update((current) => current.filter((novel) => novel.id !== deletedId));
                catalog.update((current) => current.filter((novel) => novel.id !== deletedId));
                filteredCatalog.update((current) => current.filter((novel) => novel.id !== deletedId));
              }
              void refreshNovelLists();
            }}
          />
        )}
        {page === "admin" && (isAdmin ? <AdminPage onPlay={(id) => openPlayer(id)} /> : isAuthEnabled && (sessionLoading || (user && !meLoaded)) ? (
          // The admin flag is not known yet — don't flash the landing page at an admin.
          <AdminPageSkeleton />
        ) : <LandingPage
          catalogNovels={catalog.novels}
          catalogLoaded={catalog.loaded}
          onNavigate={setPage}
          onPlay={openPlayer}
          onOpenDetail={(novelId) => {
            setSelectedNovelId(novelId);
            setPage("detail");
          }}
          onOpenAuthor={openAuthor}
        />)}
        {page === "profile" && (
          <ProfilePage
            pagination={myNovels}
            novels={myNovels.novels}
            source={myNovels.source}
            loading={!myNovels.loaded}
            currentOwnerId={currentOwnerId}
            onNavigate={guardedNavigate}
            onPlay={openPlayer}
            onOpenDetail={(novelId) => {
              setSelectedNovelId(novelId);
              setDetailFrom("profile");
              setPage("detail");
            }}
            onCreate={() => setPage("create")}
          />
        )}
        </>
        )}
        </Suspense>
      </main>
      {!FOOTERLESS_PAGES.has(page) && <SiteFooter onNavigate={guardedNavigate} />}
      {/* Compact and at the top while reading, clear of the choices. */}
      <CookieBanner onNavigate={guardedNavigate} compact={page === "player"} />
      {genModal && (
        <GenerationStartedDialog
          estimate={genModal.estimate}
          suggested={genModal.suggested}
          onPlay={(novelId) => {
            setGenModal(null);
            openPlayer(novelId);
          }}
          onClose={() => setGenModal(null)}
        />
      )}
    </div>
  );
}
